import { FunctionComponent, useEffect, useState } from 'react'
import styles from './groupList.module.scss'
import { DaycareGroup, DaycareTrustee } from '@/types/DaycareGroup'
import Title from '@/components/_custom/Title/title'
import Indicator from '@/components/_custom/Indicator/indicator'
import Desktop from '@/modules/desktopMobile/desktop'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import Mobile from '@/modules/desktopMobile/mobile'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HolidayVillage from '@mui/icons-material/HolidayVillage'
import AddGroupDialog from './Dialog/addGroupDialog'
import DaycareListDialog from './Dialog/daycareListDialog'
import { Daycare } from '@/types/Daycare'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { Role } from '@/types/Role'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface GroupListProps {
    groupList: Array<DaycareGroup>
    refetchGroups: () => void
}

const GroupList: FunctionComponent<GroupListProps> = ({ groupList, refetchGroups }) => {
    const navigate: NavigateFunction = useNavigate()

    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess } = useNotification()

    const [groupData, setGroupData] = useState<DaycareGroup[]>([])

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    useEffect(() => {
        setGroupData(
            groupList
                .filter((group: DaycareGroup) => group.active)
                .sort((a: DaycareGroup, b: DaycareGroup) => a.name.localeCompare(b.name))
        )
    }, [groupList])

    const [isAddGroupDialogOpen, setIsAddGroupDialogOpen] = useState<boolean>(false)
    const [isDaycareListDialogOpen, setIsDaycareListDialogOpen] = useState<boolean>(false)

    const columnsWeb: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structureCount'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openDaycareList(params.row.daycares)}>
                    {params?.row.daycares ? params.row.daycares.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 300,
            maxWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <a
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    href={`/group?group=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/group?group=${params.row.id}`)
                        }
                    }}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('emailAdmin'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.trustees.find((trustee: DaycareTrustee) => trustee.main)?.email ? (
                        <a
                            href={`mailto:${
                                params.row.trustees.find((trustee: DaycareTrustee) => trustee.main)?.email
                            }`}
                        >
                            {params.row.trustees.find((trustee: DaycareTrustee) => trustee.main)?.email}
                        </a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phoneAdmin'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.trustees.find((trustee: DaycareTrustee) => trustee.main)?.phone ? (
                        <a href={`tel:${params.row.trustees.find((trustee: DaycareTrustee) => trustee.main)?.phone}`}>
                            {params.row.trustees
                                .find((trustee: DaycareTrustee) => trustee.main)
                                ?.phone.match(/.{1,2}/g)
                                .join(' ')}
                        </a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'active',
            headerName: t('activeGroup'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.CheckContainer}>
                    {params.row.active ? <Check className={styles.GreenIcon} /> : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/group?group=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/group?group=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const columnsMobile: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structureCount'),
            headerClassName: headerClassName,
            width: 50,
            maxWidth: 100,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openDaycareList(params.row.daycares)}>
                    {params?.row.daycares ? params.row.daycares.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 100,
            minWidth: 100,
            maxWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <a
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    href={`/group?group=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/group?group=${params.row.id}`)
                        }
                    }}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 50,
            maxWidth: 100,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/group?group=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/group?group=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const [showDaycares, setShowDaycares] = useState<Daycare[]>([])

    function openDaycareList(daycares: Daycare[]): void {
        if (daycares && daycares.length > 0) {
            setShowDaycares(daycares)
            setIsDaycareListDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    function resetFilters(): void {
        setGroupData(groupList.sort((a: DaycareGroup, b: DaycareGroup) => a.name.localeCompare(b.name)))
    }

    function activeGroupsFilter(): void {
        setGroupData(
            groupList
                .filter((group: DaycareGroup) => group.active)
                .sort((a: DaycareGroup, b: DaycareGroup) => a.name.localeCompare(b.name))
        )
    }

    return (
        <div className={styles.DataGridContainer}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title}>
                    <Title text={t('myGroups')} />
                </div>
                <ButtonTextIcon type={'add'} onClick={() => setIsAddGroupDialogOpen(true)} className={styles.Button} />
                {isAddGroupDialogOpen && (
                    <AddGroupDialog
                        handleClose={() => setIsAddGroupDialogOpen(false)}
                        open={isAddGroupDialogOpen}
                        refetchGroups={refetchGroups}
                    />
                )}
            </div>
            <div className={styles.IndicatorsContainer}>
                <a onClick={resetFilters} className={styles.Clickable}>
                    <Indicator count={groupList.length} text={groupList.length > 1 ? t('groups') : t('group')} />
                </a>
                <a onClick={activeGroupsFilter} className={styles.Clickable}>
                    <Indicator
                        count={groupList.filter((group: DaycareGroup) => group.active).length}
                        text={
                            groupList.filter((group: DaycareGroup) => group.active).length > 1
                                ? t('activeGroups')
                                : t('activeGroup')
                        }
                    />
                </a>
            </div>
            <Desktop>
                <StyledDataGrid
                    autoHeight
                    columns={columnsWeb}
                    rows={groupData}
                    disableDensitySelector
                    disableColumnMenu
                    slots={{ toolbar: QuickSearchToolbar }}
                    showToolbar
                    sx={{ '--DataGrid-overlayHeight': '300px' }}
                    localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                    rowHeight={80}
                    className={styles.DataGrid}
                    getRowClassName={(params: GridRowClassNameParams): string => {
                        return `daycare-active-${params.row.active}`
                    }}
                />
            </Desktop>
            <Mobile>
                <StyledDataGrid
                    autoHeight
                    columns={columnsMobile}
                    rows={groupData}
                    disableDensitySelector
                    disableColumnMenu
                    slots={{ toolbar: QuickSearchToolbar }}
                    showToolbar
                    sx={{ '--DataGrid-overlayHeight': '300px' }}
                    localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                    rowHeight={80}
                    className={styles.DataGrid}
                />
            </Mobile>
            <DaycareListDialog
                isDialogOpen={isDaycareListDialogOpen}
                handleCloseDialog={() => setIsDaycareListDialogOpen(false)}
                daycares={showDaycares}
            />
        </div>
    )
}

export default GroupList
